import bs58 from 'bs58';
import { loadContacts } from '../db/ContactStorage';
import { uint8ArraysEqual } from '../utils/CryptoHelpers';
import { checkHandshakeStatus } from "../utils/HandshakeManager";

/**
 * Enriches participant public view keys with contact metadata where possible.
 *
 * @param {Object} args
 * @param {Uint8Array[]} args.participantKeys - Array of public view keys.
 * @param {string} args.userId
 * @param {string} args.password
 * @returns {Promise<Array<{ name: string, identityViewPublicKey: Uint8Array }>>}
 */
export async function getEnrichedParticipants({ participantKeys, userId, password }) {
  if (!Array.isArray(participantKeys)) {
    throw new Error("participantKeys must be an array");
  }

  for (const key of participantKeys) {
    if (!(key instanceof Uint8Array)) {
      throw new Error("All participant keys must be Uint8Array instances");
    }
  }

  const allContacts = await loadContacts({ userId, password });

  return participantKeys.map((key) => {
    const contact = allContacts.find((c) => uint8ArraysEqual(c.identityViewPublicKey, key));

    return contact
      ? contact
      : {
          name: bs58.encode(key).slice(0, 10),
          identityViewPublicKey: key,
        };
  });
}

/**
 * Removes the user's own view key from a list of view keys.
 *
 * @param {Uint8Array[]} viewKeys - All participant view public keys
 * @param {Uint8Array} ownViewKey - Your own view public key
 * @returns {Uint8Array[]} - Filtered list without own key
 */
export function removeOwnViewKey(viewKeys, ownViewKey) {
  return viewKeys.filter((key) => !uint8ArraysEqual(key, ownViewKey));
}

/**
 * Returns handshake status per participant view key.
 * Only supports 1:1 conversations.
 *
 * @param {Object} args
 * @param {Uint8Array[]} args.participantKeys - Array of participant public view keys
 * @param {string} args.conversationId - ID of the conversation
 * @param {string} args.userId - Current user's ID
 * @param {string} args.password - Current user's password
 * @returns {Promise<Array<{ viewKey: Uint8Array, status: string }>>}
 */
export async function getParticipantsHandshakeStatus({
  participantKeys,
  conversationId,
  userId,
  password,
}) {
  if (!Array.isArray(participantKeys)) {
    throw new Error("participantKeys must be an array");
  }

  const results = await Promise.all(
    participantKeys.map(async (viewKey) => { 
      try {
        return await checkHandshakeStatus({
          conversationId,
          contactIdentityViewPublicKey: viewKey,
          password,
          userId,
        });
      } catch (err) {
        console.warn("Failed to check handshake for key:", viewKey, err);
      }
    })
  );

  return results;
}